import * as React from 'react';
import { connect } from 'react-redux';
import currency from 'currency.js';
import { isNil, throttle } from 'lodash';

import { FormField } from 'components/form';
import InfoNote from 'components/info-note/InfoNote';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import Input from 'components/inputs/input/Input';
import InputRadio from 'components/inputs/input-radio/InputRadio';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import BulkOfferCustomizedFormField from '../../bulk/bulk-offer-customized-form-field/BulkOfferCustomizedFormField';

import { renderPrice, renderPriceRange } from 'utils/helper/price';
import {
  LONG_STRING_MAX_LENGTH,
  isIntegerMoneySum,
  isOneOf,
  isPositiveNumber,
  isRequired,
  isRequiredRichText,
  maxLength
} from 'utils/validators';
import { __t, applyTranslations } from 'utils/language';
import { getPlainText } from 'utils/editor';
import { getBulkOfferSelectedSubcampaigns, sumBulkOfferBudgets } from 'utils/bulk-offer';
import {
  SUBCAMPAIGN_OFFER_TYPE_OFFER,
  SUBCAMPAIGN_OFFER_TYPE_OTHER,
  SUBCAMPAIGN_OFFER_TYPE_QUOTE
} from 'utils/subcampaign';

import type {
  LocationWindow,
  SubcampaignBulkOfferStore,
  SubcampaignOfferStatus,
  SubcampaignOfferType,
  TextEntry
} from 'types';
import type { OfferAdditionalCompensationData } from 'modules/subcampaigns/components/forms/offer-additional-compensation/types';

import type { Dispatch } from 'redux';
import type { SyntheticEvent } from 'react';

applyTranslations(require('./offer-type-form.i18n.json'));

type CustomValidation = {
  valid: boolean,
  message: string
};

type OwnProps = {
  type: SubcampaignOfferType | undefined | null,
  perks: string | undefined | null,
  budget: number | undefined | null,
  usedBudget: number | null,
  additionalCompensation: OfferAdditionalCompensationData,
  state: SubcampaignOfferStatus,
  sentOfferType: SubcampaignOfferType | undefined | null,
  remainingBudget: number,
  totalBudget: number | undefined | null,
  initialBudget: number | undefined | null,
  isCopiedOffer?: boolean,
  onEdit: (a: any) => void,
  priceRecommendation: {
    min: number,
    max: number
  },
  hideInfoNote?: boolean,
  isBulkOfferActive: boolean,
  isQuoteProvidedByCreator: boolean,
  isResendingCancelled: boolean,
  renderCustomizeButton?: () => React.ReactNode
};

type StateProps = {
  bulkOfferStore: SubcampaignBulkOfferStore,
  location: LocationWindow
};

type Props = {
  dispatch: Dispatch
} & OwnProps & StateProps;

type State = {
  type: SubcampaignOfferType,
  perks: string,
  budget: string | null,
  showRichTextForPerks: boolean
};

const BUDGET_FORM_FIELD_PROPS: Partial<React.ComponentProps<typeof FormField>> = {
  label: __t('offer_type_form.amount'),
  labelType: 'highlighted',
  showRequiredMark: true,
  showValidationIcon: false,
  direction: 'column',
  fontSize: 'small'
};

export class OfferTypeForm extends React.Component<Props, State> {
  throttledHandleEdit: () => void;

  constructor(props: Props) {
    super(props);

    const perks = props.perks || '';
    const isSavedDraft = (props.location.querystring || '').includes('draft=open');

    // allow `0` in the budget field under applicable conditions
    const budget = Boolean(props.isCopiedOffer) || isSavedDraft || props.state !== 'draft'
      ? String(props.budget)
      : null;

    this.state = {
      type: props.type || 'offer',
      perks,
      budget,
      showRichTextForPerks: Boolean(perks) && getPlainText(perks).trim() !== perks.trim()
    };

    this.throttledHandleEdit = throttle(this.handleEdit, 1000);
  }

  getCurrentBudgetValue() {
    const { initialBudget, usedBudget } = this.props;
    const { budget } = this.state;

    return currency(usedBudget ?? 0)
      .subtract(currency(initialBudget || 0))
      .add(currency(Number(budget)))
      .value;
  }

  getBulkOfferUsedBudget() {
    const { bulkOfferStore, usedBudget } = this.props;
    const { budget } = this.state;

    const bulkSum = sumBulkOfferBudgets(getBulkOfferSelectedSubcampaigns(bulkOfferStore), bulkOfferStore.customizedFieldsByIdMap, Number(budget || 0));
    return currency(usedBudget ?? 0)
      .add(currency(bulkSum))
      .value;
  }

  isCustomValidation = (
    budget: string | null
  ): CustomValidation => {
    const message = __t('offer_type_form.offer_zero_no_voucher_amount_error');

    if (isNil(budget)) {
      return {
        valid: false,
        message
      };
    }

    const { additionalCompensation } = this.props;
    const isValid = !(Number(budget) === 0 && Number(additionalCompensation.voucherAmount) === 0);

    return {
      valid: isValid,
      message
    };
  };

  handleOfferTypeChanged = (event: SyntheticEvent<TextEntry>): void => {
    const value = event.currentTarget.value;
    let type: SubcampaignOfferType = 'other';

    switch (value) {
      case 'quote': {
        type = 'quote';
        break;
      }
      case 'offer': {
        type = 'offer';
        break;
      }
      default:
        // default type is already set
    }

    this.setState({
      type
    }, this.handleEdit);
  };

  handleBudgetChanged = (event: SyntheticEvent<TextEntry>): void => {
    this.setState({
      budget: event.currentTarget.value
    }, this.throttledHandleEdit);
  };

  handlePerksChanged = (value: string): void => {
    if (this.state.perks !== value) {
      this.setState({
        perks: value
      }, this.throttledHandleEdit);
    }
  };

  handlePerksInputChanged = (event: SyntheticEvent<TextEntry>): void => {
    this.handlePerksChanged(event.currentTarget.value);
  };

  handleEdit = () => {
    const { onEdit } = this.props;
    const { type, budget, perks } = this.state;

    switch (type) {
      case SUBCAMPAIGN_OFFER_TYPE_OFFER:
        onEdit({
          type,
          budget: Number(budget)
        });
        break;
      case SUBCAMPAIGN_OFFER_TYPE_OTHER:
        onEdit({
          type,
          perks
        });
        break;
      case SUBCAMPAIGN_OFFER_TYPE_QUOTE:
        onEdit({
          type
        });
        break;
      default:
    }
  };

  render() {
    const {
      totalBudget,
      priceRecommendation,
      sentOfferType,
      hideInfoNote,
      isBulkOfferActive,
      isQuoteProvidedByCreator,
      isResendingCancelled,
      renderCustomizeButton
    } = this.props;
    const { type, perks, budget, showRichTextForPerks } = this.state;
    const skipQuote = !isResendingCancelled && (
      sentOfferType === 'offer'
      || sentOfferType === 'other'
      || (sentOfferType === 'quote' && isQuoteProvidedByCreator)
    );
    const availableOfferTypes: Array<SubcampaignOfferType> = [
      'offer',
      ...(skipQuote ? [] : ['quote' as SubcampaignOfferType]),
      'other'
    ];
    const isIntegerBudgetValidation = isIntegerMoneySum(budget);

    return (
      <section
        className="offer-form"
        data-qa="offer-form"
      >
        {!hideInfoNote && (
          <div className="offer-form__info-note">
            <InfoNote type="warning">
              {__t('offer_type_form.change_budget_info')}
            </InfoNote>
          </div>
        )}
        <div className="offer-form__main-section">
          <FormField
            label={__t('offer_type_form.label')}
            labelType="highlighted"
            showRequiredMark
            direction="column"
            fontSize="small"
            showValidationIcon={false}
            validators={[
              isOneOf(type, availableOfferTypes, __t('offer_type_form.offer_type_missing_error'))
            ]}
          >
            {availableOfferTypes.map((offerType: SubcampaignOfferType) => (
              <InputRadio
                key={`offer-type--${offerType}`}
                name="offer-type"
                value={String(offerType)}
                id={`offer-type-chooser--${offerType}`}
                checked={this.state.type === offerType}
                label={__t(`offer_type_form.offer_type_chooser.${offerType}`)}
                onChange={this.handleOfferTypeChanged}
              />
            ))
            }
          </FormField>
          {
            type === SUBCAMPAIGN_OFFER_TYPE_OFFER && (
              <div className="offer-form__offer">
                <div className="offer-form__offer-section">
                  <BulkOfferCustomizedFormField
                    fieldName="budget"
                    formFieldProps={BUDGET_FORM_FIELD_PROPS}
                    isBulkOfferActive={isBulkOfferActive}
                  >
                    <div className="offer-form__budget-field">
                      <FormField
                        {...BUDGET_FORM_FIELD_PROPS}
                        validators={[
                          isRequired(budget),
                          isPositiveNumber(budget),
                          isIntegerMoneySum(budget),
                          this.isCustomValidation(budget)
                        ]}
                        immediateValidationError={(isIntegerBudgetValidation.valid) ? '' : isIntegerBudgetValidation.message}
                      >
                        <Input
                          hint="€"
                          hintAlign="right"
                          value={budget || ''}
                          onChange={this.handleBudgetChanged}
                          onBlur={this.handleEdit}
                          width="responsive"
                          data={{ qa: 'offer-form__amount-input' }}
                        />
                        {
                          (priceRecommendation.min > 0) && (priceRecommendation.max > 0) && (
                            <div className="offer-form__offer-recommendation">
                              <div>{__t('offer_type_form.price_recommendation1')}</div>
                              <span>
                                {renderPriceRange(
                                  {
                                    min: priceRecommendation.min,
                                    max: priceRecommendation.max,
                                    customTemplate: __t('offer_form.price_recommendation2')
                                  }
                                )}
                              </span>
                            </div>
                          )
                        }
                      </FormField>
                    </div>
                  </BulkOfferCustomizedFormField>
                </div>
                <div className="offer-form__offer-section offer-form__offer-section--expand">
                  <div className="offer-form__budget">
                    <span>{__t('offer_type_form.remaining_budget')}</span>
                    <span className="offer-form__budget-amount">
                      {renderPrice(
                        isBulkOfferActive ? this.getBulkOfferUsedBudget() : this.getCurrentBudgetValue()
                      )}
                    </span>
                  </div>
                  <div className="offer-form__budget">
                    <span>{__t('offer_form.total_budget')}</span>
                    <span className="offer-form__budget-amount">
                      {renderPrice(totalBudget)}
                    </span>
                  </div>
                </div>
              </div>
            )
          }
          {
            type === SUBCAMPAIGN_OFFER_TYPE_QUOTE && (
              <div className="offer-form__quote">
                <span>{__t('offer_type_form.request_quote_description')}</span>
              </div>
            )
          }
          {
            type === SUBCAMPAIGN_OFFER_TYPE_OTHER && (
              <div className="offer-form__other">
                <FormField
                  label={__t('offer_type_form.description')}
                  labelType="highlighted"
                  showRequiredMark
                  showValidationIcon={false}
                  direction="column"
                  fontSize="small"
                  validators={[
                    isRequiredRichText(perks),
                    maxLength(perks, LONG_STRING_MAX_LENGTH)
                  ]}
                >
                  {
                    showRichTextForPerks ?
                      <RichTextEditor
                        canEdit
                        liveUpdate
                        value={perks}
                        onChange={this.handlePerksChanged}
                        placeholder={__t('offer_type_form.other_arrangement_placeholder')}
                      /> :
                      <Input
                        multiline
                        width="responsive"
                        value={perks}
                        onChange={this.handlePerksInputChanged}
                        placeholder={__t('offer_type_form.other_arrangement_placeholder')}
                      />
                  }
                </FormField>
              </div>
            )
          }
          {
            type === SUBCAMPAIGN_OFFER_TYPE_OFFER && isBulkOfferActive && renderCustomizeButton &&
              <React.Fragment>
                <VerticalSpacing type="medium" />
                {renderCustomizeButton()}
              </React.Fragment>
          }
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ bulkOffer, router }): StateProps => ({
  bulkOfferStore: bulkOffer,
  location: router.location
});

export default connect(mapStateToProps)(OfferTypeForm);
